// src/pages/IncludeWords.tsx
import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, TextField, Modal, Box, IconButton, Paper, Alert, Grid, SxProps, Theme, Pagination
} from '@mui/material';
import { Edit, Delete, Info, Visibility } from '@mui/icons-material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';

interface IncludeWordsSet {
  id: string;
  set_name: string;
  words: string[];
}

interface IncludeWordsListResponse {
  sets: IncludeWordsSet[];
  total_count: number;
}

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: "50%",
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 500 },
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '12px',
  maxHeight: '90vh',
  overflow: 'auto'
};

const IncludeWords: React.FC = () => {
  const [sets, setSets] = useState<IncludeWordsSet[]>([]);
  const [totalSets, setTotalSets] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' | 'info' } | null>(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openWordsModal, setOpenWordsModal] = useState(false);
  const [selectedWords, setSelectedWords] = useState<string[]>([]);
  const [editSetId, setEditSetId] = useState<string | null>(null);
  const [setName, setSetName] = useState('');
  const [words, setWords] = useState('');
  const auth = useContext(AuthContext);
  const TIMEOUT = 10000;

  // Пагинация
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    fetchSets();
  }, [page]);

  const fetchSets = async () => {
    if (!auth?.isAuthenticated) {
      setMessage({ text: 'Авторизуйтесь для просмотра наборов', type: 'error' });
      return;
    }
    setIsLoading(true);
    try {
      const response = await api.get<IncludeWordsListResponse>('/includewords', {
        params: { limit: pageSize, offset: (page - 1) * pageSize },
        timeout: TIMEOUT
      });
      setSets(response.data.sets || []);
      setTotalSets(response.data.total_count || 0);
      setMessage(null);
    } catch (error: any) {
      console.error('Ошибка загрузки:', error);
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const createSet = async () => {
    if (!setName.trim()) {
      setMessage({ text: 'Введите название набора', type: 'error' });
      return;
    }
    setIsLoading(true);
    try {
      const wordsArray = words.split(/[\s,]+/).filter(w => w.trim());
      const response = await api.post('/includewords', { set_name: setName, words: wordsArray }, { timeout: TIMEOUT });
      setSets(prev => [...prev, response.data]);
      setMessage({ text: 'Набор создан!', type: 'success' });
      setOpenCreateModal(false);
      resetForm();
    } catch (error: any) {
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const updateSet = async () => {
    if (!editSetId) return;
    setIsLoading(true);
    try {
      const wordsArray = words.split(/[\s,]+/).filter(w => w.trim());
      const response = await api.put(`/includewords/${editSetId}`, {
        set_name: setName || undefined,
        words: wordsArray.length ? wordsArray : undefined
      }, { timeout: TIMEOUT });
      setSets(prev => prev.map(s => s.id === editSetId ? response.data : s));
      setMessage({ text: 'Набор обновлён!', type: 'success' });
      setOpenEditModal(false);
      resetForm();
    } catch (error: any) {
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSet = async (id: string) => {
    setIsLoading(true);
    try {
      await api.delete(`/includewords/${id}`, { timeout: TIMEOUT });
      setSets(prev => prev.filter(s => s.id !== id));
      setMessage({ text: 'Набор удалён', type: 'success' });
    } catch (error: any) {
      setMessage({ text: `Ошибка: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const openEdit = (set: IncludeWordsSet) => {
    setEditSetId(set.id);
    setSetName(set.set_name);
    setWords(set.words.join(', '));
    setOpenEditModal(true);
  };

  const openWords = (words: string[]) => {
    setSelectedWords(words);
    setOpenWordsModal(true);
  };

  const resetForm = () => {
    setSetName('');
    setWords('');
    setEditSetId(null);
  };

  const gridItem: SxProps<Theme> = { display: 'flex', alignItems: 'center', width: '100%' };

  const totalPages = Math.ceil(totalSets / pageSize);

  if (isLoading && sets.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* === ОПИСАНИЕ === */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Ключевые слова: фильтрация сообщений при парсинге
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Наборы ключевых слов позволяют оставить только те сообщения, которые содержат нужные вам слова. Это критично для точного сбора данных.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Зачем нужны?</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              • Убрать мусор из парсинга<br />
              • Собрать только целевые сообщения<br />
              • Экономия времени и ресурсов
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Как использовать?</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              1. Создайте набор<br />
              2. Введите слова через запятую<br />
              3. Примените в задаче парсинга
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Примеры</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem', mb: 1 }}>
              • <code>вакансия, работа, проект</code><br />
              • <code>куплю, продам, обмен</code><br />
              • <code>дизайн, верстка, фронтенд</code>
            </Typography>
          </Box>
        </Box>

        <Box sx={{ bgcolor: '#EBF8FF', p: 3, borderRadius: 2, borderLeft: '4px solid #1E40AF' }}>
          <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Совет:</Typography>
          <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
            Используйте <strong>3–7 слов</strong> в наборе. Слишком много — риск пропустить сообщения.
          </Typography>
        </Box>

        <Typography sx={{ color: '#4B5563', mt: 3, fontStyle: 'italic' }}>
          Вам не нужно указывать одно и то же слово в различных формах - если в наборе будет "вакансия" - при парсинге в выгрузку будут включены и сообщения, в которых встречаются "вакансией", "вакансии" и т.д.
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ color: '#1E3A8A', fontWeight: 600 }}>
            Мои наборы ключевых слов
          </Typography>
          <Button
            variant="contained"
            onClick={() => setOpenCreateModal(true)}
            disabled={isLoading}
            sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
          >
            Создать набор
          </Button>
        </Box>

        <Box sx={{ p: 3 }}>
          {sets.length > 0 ? (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Название</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Слов в наборе</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }} align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sets.map(s => (
                    <TableRow key={s.id} sx={{ '&:hover': { bgcolor: '#F9FAFB' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>{s.set_name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              color: '#1E40AF',
                              fontWeight: 500,
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => openWords(s.words)}
                          >
                            {s.words.length} {s.words.length === 1 ? 'слово' : s.words.length < 5 ? 'слова' : 'слов'}
                          </Typography>
                          <Visibility fontSize="small" sx={{ color: '#1E40AF', opacity: 0.7 }} />
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => openEdit(s)}
                          disabled={isLoading}
                          sx={{ color: '#6B7280', '&:hover': { bgcolor: '#F3F4F6' } }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => deleteSet(s.id)}
                          disabled={isLoading}
                          sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FECACA' } }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Наборы не созданы. Создайте первый!
            </Typography>
          )}
        </Box>
      </Paper>

      {/* === МОДАЛКА СОЗДАНИЯ === */}
      <Modal open={openCreateModal} onClose={() => !isLoading && setOpenCreateModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 2 }}>Создать набор</Typography>
          <Grid container spacing={2}>
            <Grid size={12} sx={gridItem}>
              <TextField
                label="Название набора"
                value={setName}
                onChange={e => setSetName(e.target.value)}
                fullWidth
                inputProps={{ maxLength: 32 }}
                helperText={`${setName.length}/32`}
              />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <TextField
                label="Ключевые слова"
                value={words}
                onChange={e => setWords(e.target.value)}
                fullWidth
                multiline
                rows={4}
                placeholder="вакансия, работа, проект"
                helperText="Через запятую или пробел"
              />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <Alert severity="info" icon={<Info />} sx={{ fontSize: '0.85rem' }}>
                Сообщение пройдёт фильтр, если содержит <strong>хотя бы одно</strong> из слов.
              </Alert>
            </Grid>
            <Grid size={12} sx={gridItem}>
              <Button
                variant="contained"
                onClick={createSet}
                disabled={isLoading || !setName.trim()}
                fullWidth
                sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' }, py: 1.5 }}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Создать'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      {/* === МОДАЛКА РЕДАКТИРОВАНИЯ === */}
      <Modal open={openEditModal} onClose={() => !isLoading && setOpenEditModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 2 }}>Редактировать набор</Typography>
          <Grid container spacing={2}>
            <Grid size={12} sx={gridItem}>
              <TextField
                label="Название набора"
                value={setName}
                onChange={e => setSetName(e.target.value)}
                fullWidth
                inputProps={{ maxLength: 32 }}
                helperText={`${setName.length}/32`}
              />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <TextField
                label="Ключевые слова"
                value={words}
                onChange={e => setWords(e.target.value)}
                fullWidth
                multiline
                rows={4}
                placeholder="вакансия, работа, проект"
                helperText="Через запятую или пробел"
              />
            </Grid>
            <Grid size={12} sx={gridItem}>
              <Button
                variant="contained"
                onClick={updateSet}
                disabled={isLoading}
                fullWidth
                sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' }, py: 1.5 }}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Сохранить'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      {/* НОВАЯ МОДАЛКА: СПИСОК СЛОВ */}
      <Modal open={openWordsModal} onClose={() => setOpenWordsModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3 }}>
            Слова в наборе
          </Typography>
          <Box sx={{ maxHeight: '50vh', overflowY: 'auto', bgcolor: '#F9FAFB', p: 2, borderRadius: 2 }}>
            {selectedWords.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedWords.map((word, index) => (
                  <Box
                    key={index}
                    sx={{
                      bgcolor: '#E0E7FF',
                      color: '#1E40AF',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}
                  >
                    {word}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary">Нет слов</Typography>
            )}
          </Box>
          <Box sx={{ mt: 3, textAlign: 'right' }}>
            <Button
              variant="outlined"
              onClick={() => setOpenWordsModal(false)}
              sx={{ color: '#1E3A8A', borderColor: '#1E3A8A' }}
            >
              Закрыть
            </Button>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default IncludeWords;